import * as React from 'react';
import { twMerge } from 'tailwind-merge';

type BadgeColor = 'primary' | 'teal' | 'green' | 'yellow' | 'red' | 'blue' | 'purple' | 'neutral';
type BadgeVariant = 'soft' | 'outline';
type BadgeSize = 'xss' | 'xs' | 'sm' | 'md';

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  color?: BadgeColor;
  variant?: BadgeVariant;
  size?: BadgeSize;
  mono?: boolean;
}

const COLOR_MAP: Record<BadgeColor, { bg: string; text: string; border: string }> = {
  primary: {
    bg: 'bg-primary-3',
    text: 'text-primary-11',
    border: 'border-primary-6',
  },
  teal: {
    bg: 'bg-teal-3',
    text: 'text-teal-11',
    border: 'border-teal-6',
  },
  green: {
    bg: 'bg-green-3',
    text: 'text-green-11',
    border: 'border-green-6',
  },
  yellow: {
    bg: 'bg-yellow-3',
    text: 'text-yellow-11',
    border: 'border-yellow-6',
  },
  red: {
    bg: 'bg-red-3',
    text: 'text-red-11',
    border: 'border-red-6',
  },
  blue: {
    bg: 'bg-blue-3',
    text: 'text-blue-11',
    border: 'border-blue-6',
  },
  purple: {
    bg: 'bg-purple-3',
    text: 'text-purple-11',
    border: 'border-purple-6',
  },
  neutral: {
    bg: 'bg-surface-3',
    text: 'text-text-2',
    border: 'border-stroke-2',
  },
};

const SIZE_MAP: Record<BadgeSize, string> = {
  xss: 'text-[9px] px-1 py-px',
  xs: 'text-[10px] px-1.5 py-px',
  sm: 'text-[11px] px-2 py-0.5',
  md: 'text-xs px-2.5 py-[3px]',
};

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ color = 'neutral', variant = 'soft', size = 'sm', mono = false, children, className, ...rest }, ref) => {
    const c = COLOR_MAP[color];

    return (
      <span
        ref={ref}
        className={twMerge(
          'inline-flex items-center gap-1 rounded-[3px] border font-medium',
          SIZE_MAP[size],
          c.text,
          c.border,
          variant === 'outline' ? 'bg-transparent' : c.bg,
          mono && 'font-mono tracking-[.06em] uppercase',
          className,
        )}
        {...rest}
      >
        {children}
      </span>
    );
  },
);
Badge.displayName = 'Badge';

/* ---- Semantic badges ---- */

type Rating = 'good_fit' | 'within_reach' | 'not_a_match' | 'overqualified' | 'needs_info';

const RATING_MAP: Record<Rating, { label: string; color: BadgeColor }> = {
  good_fit: { label: 'Good Fit', color: 'green' },
  within_reach: { label: 'Within Reach', color: 'yellow' },
  not_a_match: { label: 'Not a Match', color: 'red' },
  overqualified: { label: 'Overqualified', color: 'neutral' },
  needs_info: { label: 'Needs Info', color: 'neutral' },
};

export function RatingBadge({ rating, mono = false }: { rating: Rating | string; mono?: boolean }) {
  const r = RATING_MAP[rating as Rating];

  if (!r) return null;
  return (
    <Badge color={r.color} size="xs" mono>
      {r.label}
    </Badge>
  );
}

const TIER_COLOR: Record<number, BadgeColor> = {
  1: 'green',
  2: 'blue',
  3: 'yellow',
  4: 'red',
};

export function TierBadge({ tier, mono = false }: { tier: number | null | undefined; mono?: boolean }) {
  if (tier == null) {
    return <span className="text-text-disabled text-xs">&mdash;</span>;
  }
  return (
    <Badge color={TIER_COLOR[tier] ?? 'neutral'} size="sm" mono={mono}>
      T{tier}
    </Badge>
  );
}
